import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Download, Eye, FileText, Calendar, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const mockReports = [
  { id: 1, name: 'Annual ESG Report 2022', date: '2023-03-15', type: 'Comprehensive', status: 'Completed' },
  { id: 2, name: 'Environmental Performance Q4 2022', date: '2023-01-10', type: 'Environmental', status: 'Completed' },
  { id: 3, name: 'Social Impact Assessment 2022', date: '2023-02-28', type: 'Social', status: 'Completed' },
  { id: 4, name: 'Governance Review H2 2022', date: '2023-01-31', type: 'Governance', status: 'Completed' },
  { id: 5, name: 'Carbon Footprint Analysis 2022', date: '2023-02-15', type: 'Environmental', status: 'Completed' },
  { id: 6, name: 'Quarterly ESG Update Q1 2023', date: '2023-04-15', type: 'Comprehensive', status: 'In Progress' },
];

const Reports = () => {
  const [selectedReportType, setSelectedReportType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredReports = mockReports.filter(report => 
    report.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (selectedReportType === '' || report.type === selectedReportType)
  );

  const handleGenerateReport = () => {
    console.log('Generating report:', { selectedReportType, startDate, endDate });
    // Implement report generation logic here
  };

  return (
    <div className="space-y-6 p-6">
      <h1 className="text-3xl font-bold">ESG Reports</h1>
      
      <Card>
        <CardHeader>
          <CardTitle>Generate New Report</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
            <Select value={selectedReportType} onValueChange={setSelectedReportType}>
              <SelectTrigger>
                <SelectValue placeholder="Select report type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="comprehensive">Comprehensive ESG Report</SelectItem>
                <SelectItem value="environmental">Environmental Performance Report</SelectItem>
                <SelectItem value="social">Social Impact Report</SelectItem>
                <SelectItem value="governance">Governance Overview Report</SelectItem>
                <SelectItem value="custom">Custom ESG Report</SelectItem>
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-4">
              <Input type="date" placeholder="Start Date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              <Input type="date" placeholder="End Date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <Button className="w-full" onClick={handleGenerateReport}>Generate Report</Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Reports</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Input
              type="text"
              placeholder="Search reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Report Name</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredReports.map((report) => (
                <TableRow key={report.id}>
                  <TableCell>{report.name}</TableCell>
                  <TableCell>{report.date}</TableCell>
                  <TableCell>{report.type}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      report.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {report.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline">
                            <Eye className="mr-2 h-4 w-4" />
                            View
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>{report.name}</DialogTitle>
                            <DialogDescription>
                              <div className="mt-2 space-y-2">
                                <p><FileText className="inline mr-2" /> Type: {report.type}</p>
                                <p><Calendar className="inline mr-2" /> Date: {report.date}</p>
                                <p><AlertCircle className="inline mr-2" /> Status: {report.status}</p>
                              </div>
                            </DialogDescription>
                          </DialogHeader>
                          <div className="mt-4">
                            <p>Report preview would be displayed here.</p>
                          </div>
                        </DialogContent>
                      </Dialog>
                      <Button size="sm" variant="outline">
                        <Download className="mr-2 h-4 w-4" />
                        Download
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Scheduled Reports</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span>Monthly Environmental Report</span>
              <Button size="sm" variant="outline">Edit Schedule</Button>
            </div>
            <div className="flex items-center justify-between">
              <span>Quarterly Social Impact Report</span>
              <Button size="sm" variant="outline">Edit Schedule</Button>
            </div>
            <div className="flex items-center justify-between">
              <span>Annual Comprehensive ESG Report</span>
              <Button size="sm" variant="outline">Edit Schedule</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Report Templates</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Comprehensive ESG', 'Environmental', 'Social', 'Governance', 'Carbon Footprint', 'Sustainability'].map((template) => (
              <Card key={template}>
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-2">{template} Template</h3>
                  <p className="text-sm text-gray-600 mb-4">Standardized format for {template.toLowerCase()} reporting.</p>
                  <Button size="sm" variant="outline" className="w-full">
                    Use Template
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Report Analytics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-100 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Most Downloaded</h3>
              <p>Annual ESG Report 2022</p>
              <p className="text-sm text-gray-600">Downloaded 156 times</p>
            </div>
            <div className="bg-green-100 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Highest Rated</h3>
              <p>Environmental Performance Q4 2022</p>
              <p className="text-sm text-gray-600">Average rating: 4.8/5</p>
            </div>
            <div className="bg-yellow-100 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Most Shared</h3>
              <p>Social Impact Assessment 2022</p>
              <p className="text-sm text-gray-600">Shared 89 times</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Collaboration</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4">Invite team members to collaborate on report creation and review.</p>
          <div className="flex space-x-2">
            <Input type="email" placeholder="Enter email address" className="flex-grow" />
            <Button>Invite</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;